import { Link } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import Seo, { SITE_NAME, SITE_URL } from "@/components/base/Seo";

const conditionSections = [
  {
    icon: "ri-price-tag-3-line",
    title: "Listing Validity",
    body: "Free listings are valid For Ever (conditions apply). Business and Premium plans are valid for 12 months from the date your payment is confirmed.",
  },
  {
    icon: "ri-apps-2-line",
    title: "Category Limits",
    body: "Category allowances differ by plan — Free includes 1 category, Business includes one main category plus 4 sub categories, and Premium includes 3 main categories plus 6 sub categories.",
  },
  {
    icon: "ri-refresh-line",
    title: "Renewals & Upgrades",
    body: "Paid plans can be renewed or upgraded at any time from your dashboard. Upgrades take effect immediately and unused value is adjusted against your next billing cycle.",
  },
  {
    icon: "ri-checkbox-circle-line",
    title: "Listing Approval",
    body: "Every listing is reviewed before it goes live. Listings with inaccurate, duplicate or prohibited content may be edited or removed to keep the directory trustworthy.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Fair Use",
    body: "Plan benefits are meant for a single genuine business. Misuse of free or paid benefits may lead to suspension of the listing along with the associated account.",
  },
  {
    icon: "ri-customer-service-2-line",
    title: "Content Ownership",
    body: "You remain the owner of the business details and media you upload. By publishing, you grant us permission to display that content across the directory.",
  },
];

export default function TermsPage() {
  return (
    <SiteLayout>
      <Seo
        title="Terms & Conditions"
        description="Terms and conditions for listing a business on Coastal Bizz Connect — covering listing validity, category limits, renewals, approval and fair use across South Canara, Udupi and North Canara."
        keywords="Coastal Bizz Connect terms, listing terms and conditions, business listing validity, directory terms Karnataka"
        path="/terms-and-conditions"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Terms & Conditions",
          url: `${SITE_URL}/terms-and-conditions`,
          isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
        }}
      />

      <section className="border-b border-background-200 bg-primary-900">
        <div className="mx-auto w-full max-w-[1400px] px-6 py-14 md:py-20">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-background-300" aria-label="Breadcrumb">
            <Link to="/" className="cursor-pointer transition-colors hover:text-accent-300">
              Home
            </Link>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
            <span className="font-medium text-background-100">Terms &amp; Conditions</span>
          </nav>

          <div className="mt-5 max-w-3xl">
            <h1 className="font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-4xl">
              Terms &amp; Conditions
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-background-300 md:text-base">
              The conditions that apply to free, Business and Premium listings on Coastal Bizz Connect.
              Please read them together with the plan you choose.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1400px] px-6 py-12 md:py-16">
        <div className="flex flex-col items-start gap-4 rounded-lg border border-secondary-200 bg-secondary-50 p-5 md:flex-row md:items-center md:p-6">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary-500 text-background-50">
            <i className="ri-information-line text-xl" />
          </span>
          <div>
            <h2 className="font-heading text-base font-bold text-foreground-950">
              Detailed terms are on the way
            </h2>
            <p className="mt-1 max-w-3xl text-sm leading-relaxed text-foreground-600">
              A complete, clause-by-clause Terms &amp; Conditions document will be published here
              shortly. The summary below gives you the key conditions in the meantime.
            </p>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {conditionSections.map((section) => (
            <article
              key={section.title}
              className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className={`${section.icon} text-xl`} />
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                {section.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{section.body}</p>
            </article>
          ))}
        </div>

        <div className="mt-10 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
          <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
            Questions about these terms?
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
            If anything here is unclear, or you need the full terms for a multi-location plan, our
            team is happy to help.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/faqs"
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors duration-200 hover:bg-background-50"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-question-line text-base" />
              </span>
              Read the FAQs
            </Link>
            <Link
              to="/contact"
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 transition-colors duration-200 hover:bg-accent-600"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-mail-line text-base" />
              </span>
              Contact us
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}